import { useMemo, useState } from 'react';
import {
  Calendar,
  Download,
  HelpCircle,
  PhoneMissed,
  PhoneIncoming,
  RefreshCw,
  Search,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Loader } from '@/components/ui/Loader';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { fetchUnreturnedMissedCalls } from '@/services/api';
import type { UnreturnedMissedCall } from '@/types';
import { getPresetHtmlDateRange } from '@/utils/helpers';
import { ContactNameLine } from '@/components/crm/CallerName';
import { useModuleAccess } from '@/lib/useModuleAccess';
import { loadPdfAssets } from '@/lib/pdf/pdfAssets';
import { describeFilters, normalizeFilters } from '@/lib/reports/schedule';
import {
  destinationLabel,
  formatDuration,
  hungUpByLabel,
  isReturned,
  missTypeLabel,
  missedCallsCsv,
  missedCallsPdf,
  summarizeMissedCalls,
} from '@/lib/reports/missedCallsReport';
import { downloadText } from './download';
import { ScheduleReportButton } from './ScheduleReportButton';

type DatePreset = 'today' | 'week' | 'month' | 'custom';

function defaultDateRange() {
  return getPresetHtmlDateRange('week');
}

function toYeastarDateTime(date: string, endOfDay: boolean) {
  return `${date.replace(/-/g, '/')} ${endOfDay ? '23:59:59' : '00:00:00'}`;
}

const COLUMN_HELP = {
  time: 'When the missed inbound call started on the PBX.',
  callFrom:
    'Who called in — external number, gate intercom, or caller ID name when available.',
  callTo:
    'Who the call was routed to (extension name/number, queue, or ring group). “Offline now” / presence is the extension’s status when you generate the report, not at call time.',
  destination:
    'Type of destination: Extension, Queue, or Ring Group — where inbound routing sent the call.',
  reportType:
    'Miss type from Yeastar’s Unreturned Missed Call report (No Answer, Busy, Abandoned). This can disagree with the detailed CDR — if it does, we show “≠ CDR”.',
  cdrStatus:
    'Outcome from the matched Call Detail Record (more reliable than the summary report). Common values: ABANDONED (caller hung up before answer), BUSY (destination busy/rejected), NO ANSWER (not answered), FAILED, VOICEMAIL. The line under it (“Hung up by …”) is who ended the call: External = outside caller/gate, Extension = internal phone, System = PBX timeout or forced release.',
  durations:
    'Ext ring = how long the destination phone was counted as ringing (0s means it never really rang). Call length = how long the whole call existed on the PBX (trunk/gate can stay up even when the extension ring is 0s). These are different clocks — e.g. gate waited 14s (call length) but Joanne’s phone never rang (ext ring 0s).',
  callback:
    'Whether someone from the PBX later called this missed caller back (Returned) or not yet (Unreturned), per Yeastar’s unreturned-missed rules.',
  lastCallback:
    'Timestamp of the last outbound callback attempt that Yeastar linked to this missed call. “—” means no callback was recorded.',
  whatHappened:
    'Plain-language summary: report type, CDR status, who hung up, ring vs call length, and optional current registration/presence. Use this first when diagnosing gate or 0s-ring rows.',
} as const;

function ColumnHelpHeader({
  label,
  help,
  className = '',
}: {
  label: string;
  help: string;
  className?: string;
}) {
  return (
    <th
      className={`py-3 pr-3 font-semibold ${className}`}
    >
      <span className="inline-flex items-center gap-1 normal-case tracking-normal">
        <span className="uppercase tracking-wide text-xs">{label}</span>
        <span className="relative group/tip inline-flex shrink-0">
          <button
            type="button"
            className="p-0.5 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
            aria-label={`Help: ${label}`}
          >
            <HelpCircle className="w-3.5 h-3.5" aria-hidden />
          </button>
          <span
            role="tooltip"
            className="pointer-events-none invisible opacity-0 group-hover/tip:visible group-hover/tip:opacity-100 group-focus-within/tip:visible group-focus-within/tip:opacity-100 transition-opacity absolute left-0 top-full mt-1.5 z-40 w-64 sm:w-72 p-2.5 rounded-lg bg-gray-900 text-white text-[11px] font-normal normal-case tracking-normal leading-relaxed shadow-lg"
          >
            {help}
          </span>
        </span>
      </span>
    </th>
  );
}

export function UnreturnedMissedCallReport() {
  const { session } = useModuleAccess('reporting');
  const defaults = defaultDateRange();
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const [datePreset, setDatePreset] = useState<DatePreset>('week');
  const [callFrom, setCallFrom] = useState('');
  const [callTo, setCallTo] = useState('');
  const [callToType, setCallToType] = useState('');
  const [missCallType, setMissCallType] = useState('');
  const [callbackStatus, setCallbackStatus] = useState<'' | '1' | '2'>('2');
  const [abandonTime, setAbandonTime] = useState('5');
  const [loading, setLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [rows, setRows] = useState<UnreturnedMissedCall[]>([]);

  const applyPreset = (preset: 'today' | 'week' | 'month') => {
    const range = getPresetHtmlDateRange(preset);
    setStartDate(range.startDate);
    setEndDate(range.endDate);
    setDatePreset(preset);
  };

  const summary = useMemo(() => summarizeMissedCalls(rows), [rows]);

  const scheduleFilters = {
    callFrom: callFrom.trim() || undefined,
    callTo: callTo.trim() || undefined,
    callToType: callToType || undefined,
    missCallType: missCallType || undefined,
    callbackStatus: callbackStatus ? Number(callbackStatus) : undefined,
    abandonTime: abandonTime ? Number(abandonTime) : undefined,
  };

  const exportPdf = async () => {
    try {
      missedCallsPdf({
        rows,
        filtersText: describeFilters('missed', normalizeFilters('missed', scheduleFilters)),
        startDate,
        endDate,
        customerName: session?.customer?.name,
        assets: await loadPdfAssets(),
      }).save(`unreturned-missed-calls-${startDate}-to-${endDate}.pdf`);
    } catch (error) {
      console.error(error);
      toast.error('Could not make the PDF');
    }
  };

  const generate = async () => {
    if (!startDate || !endDate) {
      toast.error('Please select start and end dates');
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      toast.error('Start date must be before end date');
      return;
    }

    setLoading(true);
    try {
      const data = await fetchUnreturnedMissedCalls({
        startTime: toYeastarDateTime(startDate, false),
        endTime: toYeastarDateTime(endDate, true),
        callFrom: callFrom.trim() || undefined,
        callTo: callTo.trim() || undefined,
        callToType: callToType || undefined,
        missCallType: missCallType || undefined,
        unreturnStatus: callbackStatus
          ? (Number(callbackStatus) as 1 | 2)
          : undefined,
        abandonTime: abandonTime ? Number(abandonTime) : undefined,
      });
      setRows(data);
      setHasGenerated(true);
      if (data.length === 0) {
        toast('No missed calls matched these filters', { icon: '📞' });
      } else {
        toast.success(`${data.length} missed call${data.length === 1 ? '' : 's'} found`);
      }
    } catch (error: any) {
      console.error(error);
      toast.error(error?.message || 'Failed to load unreturned missed call report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-xl">
            <PhoneMissed className="w-6 h-6 text-orange-600 dark:text-orange-400" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
              Unreturned Missed Calls
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Same data as Yeastar&apos;s Unreturned Missed Call report, plus CDR
              status (Busy vs No Answer), hangup reason, and a plain-language
              &quot;what happened&quot; — especially useful when ring is 0s.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {(
            [
              { id: 'today' as const, label: 'Today' },
              { id: 'week' as const, label: 'This week' },
              { id: 'month' as const, label: 'This month' },
            ] as const
          ).map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => applyPreset(p.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                datePreset === p.id
                  ? 'bg-orange-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              {p.label}
            </button>
          ))}
          <span
            className={`px-3 py-1.5 rounded-lg text-sm ${
              datePreset === 'custom'
                ? 'bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900'
                : 'text-gray-500 dark:text-gray-400'
            }`}
          >
            or pick dates
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Start date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setDatePreset('custom');
                }}
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 dark:[color-scheme:dark]"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              End date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setDatePreset('custom');
                }}
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 dark:[color-scheme:dark]"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Callback status
            </label>
            <select
              value={callbackStatus}
              onChange={(e) => setCallbackStatus(e.target.value as '' | '1' | '2')}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 dark:[color-scheme:dark]"
            >
              <option value="2">Unreturned only</option>
              <option value="1">Returned only</option>
              <option value="">All (returned + unreturned)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Exclude abandoned within (sec)
            </label>
            <Input
              type="number"
              min={0}
              value={abandonTime}
              onChange={(e) => setAbandonTime(e.target.value)}
              placeholder="e.g. 5"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Call from
            </label>
            <Input
              value={callFrom}
              onChange={(e) => setCallFrom(e.target.value)}
              placeholder="Caller number"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Call to
            </label>
            <Input
              value={callTo}
              onChange={(e) => setCallTo(e.target.value)}
              placeholder="Extension / queue / number"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Destination type
            </label>
            <select
              value={callToType}
              onChange={(e) => setCallToType(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 dark:[color-scheme:dark]"
            >
              <option value="">All destinations</option>
              <option value="Extension">Extension</option>
              <option value="Queue">Queue</option>
              <option value="RingGroup">Ring Group</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Missed call type
            </label>
            <select
              value={missCallType}
              onChange={(e) => setMissCallType(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 dark:[color-scheme:dark]"
            >
              <option value="">All types</option>
              <option value="no_answer">No Answer</option>
              <option value="abandoned">Abandoned</option>
              <option value="busy">Busy</option>
            </select>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button
            onClick={generate}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Loading…
              </>
            ) : (
              <>
                <Search className="w-4 h-4" />
                Generate report
              </>
            )}
          </Button>
          <Button
            variant="secondary"
            disabled={!hasGenerated || rows.length === 0}
            onClick={() =>
              downloadText(missedCallsCsv(rows), `unreturned-missed-calls-${startDate}-to-${endDate}.csv`, 'text/csv;charset=utf-8')
            }
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="secondary" disabled={!hasGenerated || rows.length === 0} onClick={exportPdf}>
            <Download className="w-4 h-4 mr-2" />
            Export PDF
          </Button>
          <ScheduleReportButton
            kind="missed"
            className="ml-auto self-center"
            filters={scheduleFilters}
            dateRange={datePreset === 'today' ? 'today' : datePreset === 'week' ? 'thisWeek' : datePreset === 'month' ? 'thisMonth' : undefined}
          />
        </div>
      </Card>

      {loading && (
        <Card>
          <Loader text="Loading unreturned missed calls from PBX…" />
        </Card>
      )}

      {!loading && hasGenerated && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
            <Card className="!p-4">
              <p className="text-xs text-gray-500 dark:text-gray-400">Total matches</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {summary.total}
              </p>
            </Card>
            <Card className="!p-4">
              <p className="text-xs text-gray-500 dark:text-gray-400">Unreturned</p>
              <p className="text-2xl font-bold text-orange-600 dark:text-orange-400 mt-1">
                {summary.unreturned}
              </p>
            </Card>
            <Card className="!p-4">
              <p className="text-xs text-gray-500 dark:text-gray-400">Returned</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
                {summary.returned}
              </p>
            </Card>
            <Card className="!p-4">
              <p className="text-xs text-gray-500 dark:text-gray-400">0s ext ring</p>
              <p className="text-2xl font-bold text-amber-600 dark:text-amber-400 mt-1">
                {summary.zeroRing}
              </p>
            </Card>
            <Card className="!p-4">
              <p className="text-xs text-gray-500 dark:text-gray-400">CDR busy</p>
              <p className="text-2xl font-bold text-red-600 dark:text-red-400 mt-1">
                {summary.cdrBusy}
              </p>
            </Card>
            <Card className="!p-4">
              <p className="text-xs text-gray-500 dark:text-gray-400">CDR abandoned</p>
              <p className="text-2xl font-bold text-rose-600 dark:text-rose-400 mt-1">
                {summary.cdrAbandoned}
              </p>
            </Card>
            <Card className="!p-4">
              <p className="text-xs text-gray-500 dark:text-gray-400">Report vs CDR mismatch</p>
              <p className="text-2xl font-bold text-purple-600 dark:text-purple-400 mt-1">
                {summary.mismatch}
              </p>
            </Card>
          </div>

          <Card>
            <div className="overflow-x-auto overflow-y-visible">
              <table className="min-w-full text-sm">
                <thead className="relative z-10">
                  <tr className="border-b border-gray-200 dark:border-gray-700 text-left text-gray-500 dark:text-gray-400">
                    <ColumnHelpHeader label="Time" help={COLUMN_HELP.time} />
                    <ColumnHelpHeader label="Call from" help={COLUMN_HELP.callFrom} />
                    <ColumnHelpHeader label="Call to" help={COLUMN_HELP.callTo} />
                    <ColumnHelpHeader label="Destination" help={COLUMN_HELP.destination} />
                    <ColumnHelpHeader label="Report type" help={COLUMN_HELP.reportType} />
                    <ColumnHelpHeader label="CDR status" help={COLUMN_HELP.cdrStatus} />
                    <ColumnHelpHeader label="Ext ring / call length" help={COLUMN_HELP.durations} />
                    <ColumnHelpHeader label="Callback" help={COLUMN_HELP.callback} />
                    <ColumnHelpHeader label="Last callback" help={COLUMN_HELP.lastCallback} />
                    <ColumnHelpHeader
                      label="What happened"
                      help={COLUMN_HELP.whatHappened}
                      className="pr-0"
                    />
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={10}
                        className="py-10 text-center text-gray-500 dark:text-gray-400"
                      >
                        No rows for this period / filter set.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => {
                      const returned = isReturned(row);
                      const zeroRing = (row.ring_duration || 0) <= 0;
                      const cdrDisp = String(row.cdrDisposition || '').toLowerCase();
                      const cdrBusy = cdrDisp.includes('busy');
                      const cdrAbandoned = cdrDisp.includes('abandon');
                      const hungUp = hungUpByLabel(row.cdrDisconnectedBy);
                      return (
                        <tr
                          key={row.id || `${row.time}-${row.call_from}-${row.call_to}`}
                          className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 align-top"
                        >
                          <td className="py-3 pr-3 whitespace-nowrap text-gray-900 dark:text-gray-100">
                            {row.time}
                          </td>
                          <td className="py-3 pr-3 text-gray-900 dark:text-gray-100">
                            <span className="inline-flex items-center gap-1.5">
                              <PhoneIncoming className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                              <span className="break-all">{row.call_from || '—'}</span>
                            </span>
                            <ContactNameLine value={row.call_from} />
                          </td>
                          <td className="py-3 pr-3 text-gray-900 dark:text-gray-100">
                            {row.call_to || '—'}
                            {row.extRegistered === false ? (
                              <div className="text-[11px] text-red-600 dark:text-red-400 mt-0.5">
                                Offline now
                              </div>
                            ) : row.extPresence &&
                              !/^available|—$/i.test(row.extPresence) ? (
                              <div className="text-[11px] text-amber-600 dark:text-amber-400 mt-0.5">
                                {row.extPresence} now
                              </div>
                            ) : null}
                          </td>
                          <td className="py-3 pr-3">
                            <Badge
                              variant={
                                row.call_to_type?.toLowerCase() === 'queue'
                                  ? 'info'
                                  : 'default'
                              }
                            >
                              {destinationLabel(row.call_to_type)}
                            </Badge>
                          </td>
                          <td className="py-3 pr-3">
                            <Badge
                              variant={
                                row.miss_call_type === 'busy'
                                  ? 'warning'
                                  : row.miss_call_type === 'abandoned'
                                    ? 'default'
                                    : 'danger'
                              }
                            >
                              {missTypeLabel(row.miss_call_type)}
                            </Badge>
                            {row.dispositionMismatch ? (
                              <div className="text-[11px] text-purple-600 dark:text-purple-400 mt-1">
                                ≠ CDR
                              </div>
                            ) : null}
                          </td>
                          <td className="py-3 pr-3">
                            {row.cdrDisposition ? (
                              <Badge
                                variant={
                                  cdrBusy
                                    ? 'warning'
                                    : cdrAbandoned
                                      ? 'default'
                                      : cdrDisp.includes('answer') &&
                                          !cdrDisp.includes('no')
                                        ? 'success'
                                        : 'danger'
                                }
                              >
                                {row.cdrDisposition}
                              </Badge>
                            ) : (
                              <span className="text-xs text-gray-400">No CDR match</span>
                            )}
                            {hungUp ? (
                              <div
                                className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 max-w-[12rem] leading-snug"
                                title={hungUp}
                              >
                                {hungUp}
                              </div>
                            ) : row.cdrReason ? (
                              <div
                                className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 max-w-[12rem] leading-snug"
                                title={row.cdrReason}
                              >
                                {row.cdrReason}
                              </div>
                            ) : null}
                          </td>
                          <td className="py-3 pr-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                            <div
                              className={
                                zeroRing
                                  ? 'font-semibold text-amber-700 dark:text-amber-400'
                                  : 'font-medium'
                              }
                            >
                              Ext ring: {formatDuration(row.ring_duration)}
                            </div>
                            {zeroRing ? (
                              <div className="text-[11px] text-amber-600 dark:text-amber-400">
                                phone never rang
                              </div>
                            ) : null}
                            {row.cdrDurationSeconds != null ? (
                              <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                                Call length: {formatDuration(row.cdrDurationSeconds)}
                              </div>
                            ) : (
                              <div className="text-[11px] text-gray-400 mt-0.5">
                                Call length: —
                              </div>
                            )}
                          </td>
                          <td className="py-3 pr-3">
                            <Badge variant={returned ? 'success' : 'danger'}>
                              {returned ? 'Returned' : 'Unreturned'}
                            </Badge>
                          </td>
                          <td className="py-3 pr-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                            {row.return_time || '—'}
                          </td>
                          <td className="py-3 text-gray-800 dark:text-gray-200 max-w-md">
                            <p className="text-xs leading-relaxed">
                              {row.what_happened || '—'}
                            </p>
                            {(row.srcTrunk || row.didNumber || row.extDevices) && (
                              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                                {[
                                  row.didNumber && `DID ${row.didNumber}`,
                                  row.srcTrunk && `Trunk ${row.srcTrunk}`,
                                  row.extDevices && `Devices now: ${row.extDevices}`,
                                ]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </p>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800 text-xs text-gray-500 dark:text-gray-400 space-y-1.5 leading-relaxed">
              <p className="font-medium text-gray-600 dark:text-gray-300">
                How to read this report
              </p>
              <p>
                <span className="font-medium text-gray-700 dark:text-gray-300">Ext ring</span>{' '}
                is how long the destination phone was counted as ringing.{' '}
                <span className="font-medium text-gray-700 dark:text-gray-300">Call length</span>{' '}
                is how long the call stayed up on the PBX (gate/trunk can wait even when the
                phone never rang). Hover the{' '}
                <HelpCircle className="w-3 h-3 inline-block align-text-bottom" /> icons in the
                column headers for full definitions — especially{' '}
                <span className="font-medium">CDR status</span> (and “Hung up by: external
                caller” for gate/visitor hangups).
              </p>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
